import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@shared/auth/AuthContext';
import { useHasAccounts } from '@shared/hooks/useHasAccounts';
import { useTheme } from '../../contexts/ThemeContext';

// Paths that require accounts to be enabled
const ACCOUNT_REQUIRED_PATHS = new Set([
  '/dashboard',
  '/holdings',
  '/transactions',
  '/income',
  '/gains',
  '/cost-basis',
  '/performance',
  '/rebalancer',
  '/income-estimator',
  '/taxes',
]);

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

// Simple SVG icons for navigation
const Icons = {
  Dashboard: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  Holdings: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  Transactions: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Accounts: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 17a5 5 0 0 0 10 0c0-2.76-2.24-5-5-5s-5 2.24-5 5Z" />
      <path d="M12 17a5 5 0 0 0 10 0c0-2.76-2.24-5-5-5s-5 2.24-5 5Z" />
      <path d="M7 7a5 5 0 0 0 10 0c0-2.76-2.24-5-5-5S7 4.24 7 7Z" />
    </svg>
  ),
  Gains: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  ),
  CostBasis: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M7 8h10" />
      <path d="M7 12h10" />
      <path d="M7 16h6" />
    </svg>
  ),
  Performance: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  Income: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Taxes: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  Import: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Menu: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  Close: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Sun: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  ),
  Moon: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  ChevronRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  OptionsLab: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12" />
      <path d="M8 12h8" />
      <path d="m9 9 6 6" />
      <path d="m15 9-6 6" />
    </svg>
  ),
  Watchlist: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  Market: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Candlestick chart - distinctly market/trading */}
      <path d="M9 4v4" />
      <rect x="7" y="8" width="4" height="8" />
      <path d="M9 16v4" />
      <path d="M17 6v2" />
      <rect x="15" y="8" width="4" height="6" fill="currentColor" />
      <path d="M17 14v4" />
    </svg>
  ),
  IncomeEstimator: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="14" />
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="6" y1="20" x2="6" y2="16" />
      <path d="M12 2v4M14 4h-4" />
    </svg>
  ),
  Rebalancer: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Pie chart with 3 unequal sections: small 12-3 o'clock, larger sections below */}
      <circle cx="12" cy="12" r="10" />
      <path d="M12 12L12 2" />
      <path d="M12 12L22 12" />
      <path d="M12 12L5 19" />
    </svg>
  ),
  Logout: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
};

const navGroups: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: <Icons.Dashboard /> },
      { label: 'Watchlist', path: '/watchlist', icon: <Icons.Watchlist /> },
      { label: 'Market', path: '/market', icon: <Icons.Market /> },
    ],
  },
  {
    title: 'Portfolio',
    items: [
      { label: 'Holdings', path: '/holdings', icon: <Icons.Holdings /> },
      { label: 'Transactions', path: '/transactions', icon: <Icons.Transactions /> },
      { label: 'Rebalancer', path: '/rebalancer', icon: <Icons.Rebalancer /> },
    ],
  },
  {
    title: 'Analysis',
    items: [
      { label: 'Gains', path: '/gains', icon: <Icons.Gains /> },
      { label: 'Cost Basis', path: '/cost-basis', icon: <Icons.CostBasis /> },
      { label: 'Performance', path: '/performance', icon: <Icons.Performance /> },
      { label: 'Options Lab', path: '/options-lab', icon: <Icons.OptionsLab /> },
    ],
  },
  {
    title: 'Income & Taxes',
    items: [
      { label: 'Income', path: '/income', icon: <Icons.Income /> },
      { label: 'Income Estimator', path: '/income-estimator', icon: <Icons.IncomeEstimator /> },
      { label: 'Taxes', path: '/taxes', icon: <Icons.Taxes /> },
    ],
  },
  {
    title: 'Tools',
    items: [
      { label: 'Settings', path: '/settings', icon: <Icons.Settings /> },
      { label: 'Import', path: '/import', icon: <Icons.Import /> },
      { label: 'Logout', path: '#', icon: <Icons.Logout /> },
    ],
  },
];

interface SideNavProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export const SideNav: React.FC<SideNavProps> = ({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
}) => {
  const { theme, toggleTheme } = useTheme();
  const { logout } = useAuth();
  const location = useLocation();
  const { hasAccounts } = useHasAccounts();

  // Check if a nav item should be disabled (no accounts)
  const isNavItemDisabled = (path: string): boolean => {
    return !hasAccounts && ACCOUNT_REQUIRED_PATHS.has(path);
  };

  // Close mobile nav when route changes
  useEffect(() => {
    onCloseMobile();
  }, [location.pathname, onCloseMobile]);

  // Handle escape key to close mobile nav
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) {
        onCloseMobile();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileOpen, onCloseMobile]);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-[var(--sidebar-overlay-bg)] z-[99] opacity-100 transition-opacity duration-[var(--sidebar-transition)]" onClick={onCloseMobile} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen ${collapsed ? 'w-16' : 'w-[210px]'} bg-[var(--sidebar-bg)] border-r border-[var(--sidebar-border)] flex flex-col z-[100] transition-[width,transform] duration-[var(--sidebar-transition)] overflow-hidden ${mobileOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'}`}
        aria-label="Main navigation"
      >
        {/* Logo / Brand */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--sidebar-border)] min-h-[60px]">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="text-2xl flex-shrink-0 text-[var(--sidebar-text)] flex items-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                {/* Flame */}
                <path d="M12 2C12 2 9 5.5 9 8c0 1.5 1.5 3 3 3s3-1.5 3-3c0-2.5-3-6-3-6z" />
                {/* Candle body */}
                <rect x="8" y="11" width="8" height="11" rx="1" />
                {/* Drip detail */}
                <path d="M10 11v2M14 11v1" />
              </svg>
            </span>
            {!collapsed && <span className="text-lg font-bold text-[var(--sidebar-text)] whitespace-nowrap">Luminaria</span>}
          </div>
          <button
            className="hidden max-md:flex bg-transparent border-none text-[var(--sidebar-text)] cursor-pointer p-2 rounded-sm hover:bg-[var(--sidebar-item-hover)]"
            onClick={onCloseMobile}
            aria-label="Close navigation"
          >
            <Icons.Close />
          </button>
        </div>

        {/* Navigation Groups */}
        <nav className="flex-1 overflow-y-auto py-3">
          {navGroups.map((group) => (
            <div key={group.title} className="mb-4">
              {!collapsed && <div className="py-2 px-4 text-xs font-bold text-[var(--sidebar-group-text)] uppercase tracking-wider">{group.title}</div>}
              <ul className="list-none m-0 p-0">
                {group.items.map((item) => {
                  const disabled = isNavItemDisabled(item.path);
                  const baseClasses = `flex items-center gap-3 py-3 px-4 text-[var(--sidebar-text)] no-underline text-sm font-medium transition-[background,color] duration-fast border-l-[3px] border-transparent ${collapsed ? 'justify-center !p-3 border-l-0 rounded-sm mx-2 my-1' : ''}`;
                  const hoverClasses = 'hover:bg-[var(--sidebar-item-hover)]';
                  const activeClasses = 'bg-[var(--sidebar-item-active-bg)] !text-[var(--sidebar-item-active)] !border-l-[var(--sidebar-item-active)]';
                  const disabledClasses = 'opacity-40 cursor-not-allowed select-none hover:!bg-transparent';
                  const collapsedActiveClasses = collapsed ? '!border-l-0 rounded-sm' : '';

                  return (
                    <li key={item.path}>
                      {item.label === 'Logout' ? (
                        <button
                          className={`${baseClasses} ${hoverClasses} w-full text-left bg-transparent border-none border-l-[3px] border-transparent cursor-pointer font-[inherit] ${collapsed ? '!w-auto' : ''}`}
                          onClick={() => logout()}
                          title={collapsed ? item.label : undefined}
                        >
                          <span className="flex items-center justify-center flex-shrink-0 w-6 h-6">{item.icon}</span>
                          {!collapsed && <span className="whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>}
                        </button>
                      ) : disabled ? (
                        <span
                          className={`${baseClasses} ${disabledClasses}`}
                          title={collapsed ? `${item.label} (requires account)` : 'Add an account to access this feature'}
                        >
                          <span className="flex items-center justify-center flex-shrink-0 w-6 h-6">{item.icon}</span>
                          {!collapsed && <span className="whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>}
                        </span>
                      ) : (
                        <NavLink
                          to={item.path}
                          className={({ isActive }) =>
                            `${baseClasses} ${hoverClasses} ${isActive ? `${activeClasses} ${collapsedActiveClasses}` : ''}`
                          }
                          title={collapsed ? item.label : undefined}
                        >
                          <span className="flex items-center justify-center flex-shrink-0 w-6 h-6">{item.icon}</span>
                          {!collapsed && <span className="whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>}
                        </NavLink>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer actions */}
        <div className="p-3 border-t border-[var(--sidebar-border)] flex flex-col gap-2 max-md:pb-[calc(var(--bottom-nav-height)+var(--space-3)+env(safe-area-inset-bottom))]">
          <button
            className={`flex items-center gap-3 p-3 bg-transparent border-none text-[var(--sidebar-text-muted)] text-sm cursor-pointer rounded-sm transition-[background,color] duration-fast w-full text-left hover:bg-[var(--sidebar-item-hover)] hover:text-[var(--sidebar-text)] ${collapsed ? 'justify-center' : ''}`}
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Icons.Sun /> : <Icons.Moon />}
            {!collapsed && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
          </button>
          <button
            className={`flex items-center gap-3 p-3 bg-transparent border-none text-[var(--sidebar-text-muted)] text-sm cursor-pointer rounded-sm transition-[background,color] duration-fast w-full text-left hover:bg-[var(--sidebar-item-hover)] hover:text-[var(--sidebar-text)] max-md:hidden ${collapsed ? 'justify-center' : ''}`}
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <Icons.ChevronRight /> : <Icons.ChevronLeft />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

// Mobile menu toggle button (to be placed in header on mobile)
export const MobileMenuButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button className="hidden max-md:flex items-center justify-center bg-transparent border-none text-[var(--color-text)] cursor-pointer p-2 rounded-sm hover:bg-[var(--sidebar-item-hover)]" onClick={onClick} aria-label="Open navigation">
    <Icons.Menu />
  </button>
);

export default SideNav;
